import { ChangeEvent, useMemo, useState } from 'react'

import { DropdownOption, DropdownValue } from '@inno-ui/dropdown'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ArtifactSummary,
  GET_ARTIFACTS_QUERY_KEY,
  GET_ARTIFACT_SHARES_QUERY_KEY,
  getForgeOrgId,
  useGetArtifactSharesQuery,
  useReviewerCapabilityQuery,
  useSearchArtifactUsersQuery,
  useShareArtifactMutation,
  useShareArtifactWithOrgsMutation,
  useShareArtifactWithPositionsMutation,
  useUnshareArtifactMutation,
  useUnshareArtifactOrgsMutation,
  useUnshareArtifactPositionsMutation
} from 'shared/api/forgeApi'
import { useGetHrPositionsQuery } from 'shared/api/hrApi'
import { useDebounce } from 'shared/hooks/useDebounce'

import { ArtifactUser } from '../../types'

// Org-toggle label: keyed by (gated? / shared? / status) so the call site is
// a 3-state lookup, not an if-else ladder.
const ORG_LABEL = {
  sharedOn: 'orgSharedOn',
  sharedOff: 'orgSharedOff',
  requestOff: 'org_request_off',
  requested: 'org_requested',
  declined: 'org_declined_label'
} as const

// All state, queries and mutations behind the panel — kept out of the
// component so ShareArtifactPanel.tsx stays pure layout/orchestration.
export const useShareArtifactPanel = (artifact: ArtifactSummary) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })
  const queryClient = useQueryClient()
  const publicId = artifact.public_id

  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query.trim(), 350)
  const [selectedUser, setSelectedUser] = useState<DropdownValue>(undefined)

  const { data: shares } = useGetArtifactSharesQuery(publicId)
  const userShares = useMemo(() => shares?.users ?? [], [shares])
  const orgShares = useMemo(() => shares?.orgs ?? [], [shares])
  const positionShares = useMemo(() => shares?.positions ?? [], [shares])
  const sharedPositionIds = useMemo(
    () => new Set(positionShares.map(p => p.position_id)),
    [positionShares]
  )

  const { data: hrPositions = [] } = useGetHrPositionsQuery()
  const positionValue: DropdownOption[] = useMemo(
    () =>
      positionShares.map(p => ({
        label: p.name || t('positionFallback', { id: p.position_id }),
        value: p.position_id
      })),
    [positionShares, t]
  )
  const positionItems: DropdownOption[] = useMemo(
    () => hrPositions.map(p => ({ label: p.name, value: p.id })),
    [hrPositions]
  )

  const myOrgId = getForgeOrgId()
  const isOrgShared = useMemo(
    () => Boolean(myOrgId) && orgShares.some(o => o.org_id === myOrgId),
    [orgShares, myOrgId]
  )
  const sharedIds = useMemo(
    () => new Set(userShares.map(s => s.user_id)),
    [userShares]
  )

  const { data: results = [], isFetching: isSearching } =
    useSearchArtifactUsersQuery(debouncedQuery)

  const { data: capability } = useReviewerCapabilityQuery()
  const reviewScope = capability?.review_scope ?? 'any'
  // org shares are gated (need review) unless review is off
  const orgGated = reviewScope === 'any' || reviewScope === 'org'

  const invalidateShares = () => {
    queryClient.invalidateQueries({
      queryKey: [GET_ARTIFACT_SHARES_QUERY_KEY, publicId]
    })
    // a share can move the widget into review; refresh the artifact so the
    // status banner reflects the new state
    queryClient.invalidateQueries({ queryKey: [GET_ARTIFACTS_QUERY_KEY] })
  }

  const shareMutation = useShareArtifactMutation({
    onSuccess: invalidateShares
  })
  const unshareMutation = useUnshareArtifactMutation({
    onSuccess: invalidateShares
  })
  const shareOrgsMutation = useShareArtifactWithOrgsMutation({
    onSuccess: invalidateShares
  })
  const unshareOrgsMutation = useUnshareArtifactOrgsMutation({
    onSuccess: invalidateShares
  })
  // a position-picker change fires a share and an unshare mutation in
  // parallel (see handlePositionsChange) — onError also resyncs so a partial
  // failure snaps the chips back to server state instead of drifting
  const sharePositionsMutation = useShareArtifactWithPositionsMutation({
    onSuccess: invalidateShares,
    onError: invalidateShares
  })
  const unsharePositionsMutation = useUnshareArtifactPositionsMutation({
    onSuccess: invalidateShares,
    onError: invalidateShares
  })

  const busy =
    shareMutation.isPending ||
    unshareMutation.isPending ||
    shareOrgsMutation.isPending ||
    unshareOrgsMutation.isPending ||
    sharePositionsMutation.isPending ||
    unsharePositionsMutation.isPending

  const add = (user: ArtifactUser) =>
    shareMutation.mutate({ publicId, users: [user] })

  const remove = (userId: string) =>
    unshareMutation.mutate({ publicId, userIds: [userId] })

  const toggleOrg = (checked: boolean) => {
    if (!myOrgId) return
    if (checked) {
      shareOrgsMutation.mutate({
        publicId,
        orgs: [{ id: myOrgId, name: t('myOrganization') }]
      })
    } else {
      unshareOrgsMutation.mutate({ publicId, orgIds: [myOrgId] })
    }
  }

  const removePosition = (positionId: string) =>
    unsharePositionsMutation.mutate({ publicId, positionIds: [positionId] })

  // controlled multi-select: diff the next chip list against current shares
  // and fire one share + one unshare call for whatever changed
  const handlePositionsChange = (value: DropdownValue) => {
    const next = (Array.isArray(value) ? value : []) as DropdownOption[]
    const nextIds = new Set(next.map(o => String(o.value)))

    const added = next.filter(o => !sharedPositionIds.has(String(o.value)))
    const removedIds = positionShares
      .filter(p => !nextIds.has(p.position_id))
      .map(p => p.position_id)

    if (added.length) {
      sharePositionsMutation.mutate({
        publicId,
        positions: added.map(o => {
          const hrPosition = hrPositions.find(p => p.id === String(o.value))
          return {
            id: String(o.value),
            name: hrPosition?.name ?? String(o.label),
            department_id: hrPosition?.department_id
          }
        })
      })
    }
    if (removedIds.length) {
      unsharePositionsMutation.mutate({ publicId, positionIds: removedIds })
    }
  }

  const dropdownItems: DropdownOption[] = results
    .filter(user => !sharedIds.has(user.id))
    .map(user => ({
      label: user.name,
      subtitle: user.email ?? undefined,
      value: user.id,
      avatar: user.avatar || '_'
    }))

  const handleSelect = (value: DropdownValue) => {
    const option = Array.isArray(value)
      ? value[0]
      : (value as DropdownOption | undefined)
    if (!option || typeof option === 'string') return
    const user = results.find(u => u.id === option.value)
    if (user) add(user)
    setSelectedUser(undefined)
    setQuery('')
  }

  const onPeopleSearch = (event: ChangeEvent<HTMLInputElement>) =>
    setQuery(event.target.value)

  const onPeopleClear = () => {
    setSelectedUser(undefined)
    setQuery('')
  }

  const onPeopleClose = () => setQuery('')

  const status = artifact.review_status
  const isShared =
    userShares.length > 0 || isOrgShared || positionShares.length > 0

  // org toggle wording reflects the actual state — a gated org-share is a
  // *request* while pending, but must NOT read "will approve" once declined.
  // The first key is gated?/shared?/status; lookup avoids an if-else ladder.
  const orgLabelKey: keyof typeof ORG_LABEL = orgGated
    ? isOrgShared
      ? status === 'approved'
        ? 'sharedOn'
        : status === 'declined'
          ? 'declined'
          : 'requested'
      : 'requestOff'
    : isOrgShared
      ? 'sharedOn'
      : 'sharedOff'
  const orgLabel = t(ORG_LABEL[orgLabelKey])

  // Resubmit a declined widget: re-send the current shares, which flips it back
  // to pending server-side (submit_for_review). Refining via the agent also
  // re-triggers review, but this lets the owner re-request as-is.
  const resubmit = () => {
    if (isOrgShared && myOrgId) {
      shareOrgsMutation.mutate({
        publicId,
        orgs: [{ id: myOrgId, name: t('myOrganization') }]
      })
    }
    if (userShares.length) {
      shareMutation.mutate({
        publicId,
        users: userShares.map(s => ({
          id: s.user_id,
          name: s.name ?? '',
          avatar: s.avatar,
          email: s.email
        }))
      })
    }
    if (positionShares.length) {
      sharePositionsMutation.mutate({
        publicId,
        positions: positionShares.map(p => ({
          id: p.position_id,
          name: p.name ?? t('positionFallback', { id: p.position_id }),
          department_id: p.department_id
        }))
      })
    }
  }

  return {
    status,
    isShared,
    busy,
    // org
    myOrgId,
    isOrgShared,
    orgLabel,
    toggleOrg,
    // positions
    positionItems,
    positionValue,
    positionShares,
    handlePositionsChange,
    removePosition,
    // people
    dropdownItems,
    userShares,
    selectedUser,
    isSearching,
    handleSelect,
    remove,
    onPeopleSearch,
    onPeopleClear,
    onPeopleClose,
    // resubmit
    resubmit
  }
}
